import { EllipsisVertical } from 'lucide-react';
import type { ReactNode } from 'react';
import { Children, Fragment, isValidElement, useRef, useState } from 'react';

import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/popover';

function flattenChildren(children: ReactNode): ReactNode[] {
    const result: ReactNode[] = [];
    Children.forEach(children, child => {
        if (isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment) {
            result.push(...flattenChildren(child.props.children));
        } else if (child != null && child !== false) {
            result.push(child);
        }
    });
    return result;
}

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [role="button"]:not([disabled]), [tabindex="0"]:not([disabled])';

export interface OverflowMenuProps {
    children: ReactNode;
}

export function OverflowMenu({ children }: OverflowMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const allButtons = flattenChildren(children).filter(Boolean);

    if (allButtons.length === 0) {
        return null;
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const container = containerRef.current;
        if (!container) return;

        const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        const currentIndex = focusable.indexOf(e.target as HTMLElement);
        if (currentIndex === -1) return;

        let nextIndex: number | null = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            nextIndex = (currentIndex + 1) % focusable.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            nextIndex = (currentIndex - 1 + focusable.length) % focusable.length;
        } else if (e.key === 'Home') {
            nextIndex = 0;
        } else if (e.key === 'End') {
            nextIndex = focusable.length - 1;
        }

        if (nextIndex !== null) {
            e.preventDefault();
            focusable[nextIndex].focus();
        }
    };

    const handleFocus = (e: React.FocusEvent) => {
        (e.target as HTMLElement).setAttribute('data-focus-visible', 'true');
    };

    const handleBlur = (e: React.FocusEvent) => {
        (e.target as HTMLElement).removeAttribute('data-focus-visible');
    };

    return (
        <Popover
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    data-style="ghost"
                    data-active-state={isOpen ? 'on' : 'off'}
                    role="button"
                    tabIndex={0}
                    aria-label="More"
                    tooltip="More"
                >
                    <EllipsisVertical className="tiptap-button-icon" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                style={{ maxWidth: 'calc(100vw - 1rem)' }}
                onOpenAutoFocus={e => {
                    e.preventDefault();
                    const firstButton = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
                    firstButton?.focus();
                }}
            >
                <Card>
                    <CardBody>
                        <div
                            ref={containerRef}
                            role="toolbar"
                            aria-label="More toolbar options"
                            onKeyDown={handleKeyDown}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.125rem' }}
                        >
                            {allButtons}
                        </div>
                    </CardBody>
                </Card>
            </PopoverContent>
        </Popover>
    );
}

export default OverflowMenu;
